import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, X, Mail, Pencil, Star, Send, Trash2 } from 'lucide-react';
import { listUsers, inviteUser, listInvites, updateUser, resendInvite, revokeInvite } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { Avatar } from '../Avatar';
import { useUserStore } from '../../store/userStore';
import { isPersonaGated } from '../../lib/personas';
import type { EntityRole, EntityUser, FunctionalRole, Invite } from '../../types';
import { ConfirmModal } from '../ConfirmModal';

const ROLE_COLORS: Record<FunctionalRole | EntityRole, string> = {
  admin: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/30',
  member: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
  executive: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-900/30',
  product_manager: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-900/30',
  team_lead: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-900/30',
  engineer: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-900/30',
  designer: 'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-900/30',
  sales: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-900/30',
  marketing: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-900/30',
};

const ENTITY_ROLE_OPTIONS: EntityRole[] = ['admin', 'member'];

const FUNCTIONAL_ROLE_OPTIONS: FunctionalRole[] = [
  'executive',
  'product_manager',
  'team_lead',
  'engineer',
  'designer',
  'sales',
  'marketing',
];

// ── Shared role picker ─────────────────────────────────────────────────────

function RolePicker({
  entityRole,
  functionalRoles,
  primaryRole,
  onEntityRoleChange,
  onFunctionalRoleToggle,
  onPrimaryRoleChange,
}: {
  entityRole: EntityRole;
  functionalRoles: FunctionalRole[];
  primaryRole: FunctionalRole | null;
  onEntityRoleChange: (r: EntityRole) => void;
  onFunctionalRoleToggle: (fr: FunctionalRole) => void;
  onPrimaryRoleChange: (fr: FunctionalRole) => void;
}) {
  // Persona consolidation (2026-08): the Primary Role selector must read the
  // same gate as My Profile and the dashboard/nav dispatch (see
  // frontend/src/lib/personas.ts) so the two surfaces can't drift — an admin
  // should never be offered a primary option that My Profile would hide.
  // The Functional Roles checkboxes below are deliberately NOT filtered:
  // designer/team_lead/engineer/sales remain real, assignable roles, just
  // not selectable as a dashboard-determining primary. A gated role already
  // set as primary (pre-migration data) is coerced server-side on save
  // (see backend/app/routers/users.py update_user_admin) regardless of what
  // this list renders.
  const legacyPersonasEnabled = useUserStore((s) => s.sessionConfig?.legacyPersonasEnabled ?? false);
  const selectablePrimaryRoles = functionalRoles.filter(
    (fr) => !isPersonaGated(fr, legacyPersonasEnabled),
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
          Entity Role
        </label>
        <div className="flex gap-2">
          {ENTITY_ROLE_OPTIONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onEntityRoleChange(r)}
              className={`flex-1 py-1.5 rounded-lg text-meta font-medium border transition-colors capitalize ${
                entityRole === r
                  ? 'bg-primary text-white border-primary'
                  : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-primary/50'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
          Functional Roles
        </label>
        <div className="flex flex-wrap gap-2">
          {FUNCTIONAL_ROLE_OPTIONS.map((fr) => (
            <button
              key={fr}
              type="button"
              onClick={() => onFunctionalRoleToggle(fr)}
              className={`px-2.5 py-1 rounded-full text-meta font-medium border transition-colors capitalize ${
                functionalRoles.includes(fr)
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-primary/30'
              }`}
            >
              {fr.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      {selectablePrimaryRoles.length > 0 && (
        <div>
          <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
            <Star className="h-4 w-4" />
            Primary Role
            <span className="text-micro text-gray-400 ml-1">(sets default home dashboard)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {selectablePrimaryRoles.map((fr) => (
              <button
                key={fr}
                type="button"
                onClick={() => onPrimaryRoleChange(fr)}
                className={`px-2.5 py-1 rounded-full text-meta font-medium border transition-colors capitalize flex items-center gap-1 ${
                  primaryRole === fr
                    ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                    : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-amber-300'
                }`}
              >
                {primaryRole === fr && <Star className="h-4 w-4 fill-current" />}
                {fr.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Invite modal ───────────────────────────────────────────────────────────

function InviteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [entityRole, setEntityRole] = useState<EntityRole>('member');
  const [functionalRoles, setFunctionalRoles] = useState<FunctionalRole[]>([]);
  const [primaryRole, setPrimaryRole] = useState<FunctionalRole | null>(null);

  const toggleFunctionalRole = (fr: FunctionalRole) => {
    setFunctionalRoles((prev) => {
      const next = prev.includes(fr) ? prev.filter((r) => r !== fr) : [...prev, fr];
      // If primary was removed, default to first remaining
      if (primaryRole === fr || !primaryRole) {
        setPrimaryRole(next[0] ?? null);
      }
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: () => inviteUser({ email, entityRole, functionalRoles, primaryRole }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invites });
      onClose();
    },
  });

  return createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-6 w-full max-w-sm pointer-events-auto shadow-xl"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-title text-gray-900 dark:text-white">Invite Team Member</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50"
              />
            </div>

            <RolePicker
              entityRole={entityRole}
              functionalRoles={functionalRoles}
              primaryRole={primaryRole}
              onEntityRoleChange={setEntityRole}
              onFunctionalRoleToggle={toggleFunctionalRole}
              onPrimaryRoleChange={setPrimaryRole}
            />
          </div>

          {mutation.isError && (
            <p className="mt-3 text-meta text-red-500 dark:text-red-400">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex gap-2 mt-5">
            <button
              onClick={onClose}
              className="flex-1 py-2 rounded-xl text-meta text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => mutation.mutate()}
              disabled={!email || mutation.isPending}
              className="flex-1 py-2 rounded-xl text-meta font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {mutation.isPending ? 'Sending...' : 'Send Invite'}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </>,
    document.body,
  );
}

// ── Edit user modal ────────────────────────────────────────────────────────

function EditUserModal({ user, onClose }: { user: EntityUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [entityRole, setEntityRole] = useState<EntityRole>((user.entityRole ?? 'member') as EntityRole);
  const [functionalRoles, setFunctionalRoles] = useState<FunctionalRole[]>((user.functionalRoles ?? []) as FunctionalRole[]);
  const [primaryRole, setPrimaryRole] = useState<FunctionalRole | null>(
    (user.primaryRole ?? user.functionalRoles?.[0] ?? null) as FunctionalRole | null
  );

  const toggleFunctionalRole = (fr: FunctionalRole) => {
    setFunctionalRoles((prev) => {
      const next = prev.includes(fr) ? prev.filter((r) => r !== fr) : [...prev, fr];
      if (primaryRole === fr || !primaryRole) {
        setPrimaryRole(next[0] ?? null);
      }
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: () => updateUser(user.userId, { entityRole, functionalRoles, primaryRole }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users() });
      onClose();
    },
  });

  return createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-6 w-full max-w-sm pointer-events-auto shadow-xl"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-title text-gray-900 dark:text-white">Edit Roles</h3>
              <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">{user.fullName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          <RolePicker
            entityRole={entityRole}
            functionalRoles={functionalRoles}
            primaryRole={primaryRole}
            onEntityRoleChange={setEntityRole}
            onFunctionalRoleToggle={toggleFunctionalRole}
            onPrimaryRoleChange={setPrimaryRole}
          />

          {mutation.isError && (
            <p className="mt-3 text-meta text-red-500 dark:text-red-400">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex gap-2 mt-5">
            <button
              onClick={onClose}
              className="flex-1 py-2 rounded-xl text-meta text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="flex-1 py-2 rounded-xl text-meta font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving...' : 'Save Changes'}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </>,
    document.body,
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function PeopleRoles() {
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState<EntityUser | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<Invite | null>(null);
  const currentUser = useUserStore((s) => s.currentUser);
  const qc = useQueryClient();
  const { data: users, isLoading } = useQuery({
    queryKey: queryKeys.users(),
    queryFn: () => listUsers(),
  });
  const { data: invites } = useQuery({
    queryKey: queryKeys.invites,
    queryFn: listInvites,
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.invites }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeInvite(id),
    onSuccess: () => {
      setConfirmRevoke(null);
      void qc.invalidateQueries({ queryKey: queryKeys.invites });
    },
  });

  const mergedUsers = (() => {
    if (!users) return users;
    if (!currentUser) return users;
    const alreadyIncluded = users.some((u) => u.userId === currentUser.userId);
    return alreadyIncluded ? users : [currentUser, ...users];
  })();

  const pendingInvites = invites?.filter((i) => !i.acceptedAt) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-title text-gray-900 dark:text-white">Team Members</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowInvite(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Invite Member
        </motion.button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-16 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {mergedUsers?.map((user) => {
            const displayRole = user.primaryRole ?? user.functionalRoles?.[0];
            return (
              <div
                key={user.userId}
                className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 flex items-center gap-3"
              >
                <Avatar user={user} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-title text-gray-900 dark:text-white truncate">
                    {user.fullName}
                  </p>
                  <p className="text-meta text-gray-500 truncate">{user.email}</p>
                  {user.department && (
                    <p className="text-micro text-gray-400">{user.department}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                  {displayRole && (
                    <span
                      className={`text-meta px-2 py-0.5 rounded-full border capitalize flex items-center gap-1 ${
                        ROLE_COLORS[(displayRole) as FunctionalRole | EntityRole]
                      }`}
                    >
                      {user.primaryRole === displayRole && (
                        <Star className="h-2.5 w-2.5 fill-current" />
                      )}
                      {displayRole.replace(/_/g, ' ')}
                    </span>
                  )}
                  {!displayRole && (
                    <span className={`text-meta px-2 py-0.5 rounded-full border capitalize ${ROLE_COLORS[(user.entityRole ?? 'member') as EntityRole]}`}>
                      {user.entityRole ?? 'member'}
                    </span>
                  )}
                  <span
                    className={`text-meta px-2 py-0.5 rounded-full border flex-shrink-0 ${
                      user.isActive
                        ? 'text-green-700 bg-green-100 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-900/30'
                        : 'text-gray-500 bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                    }`}
                  >
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => setEditingUser(user)}
                    className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                    title="Edit roles"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pendingInvites.length > 0 && (
        <div>
          <h4 className="text-title text-gray-500 dark:text-gray-400 mb-2">
            Pending Invites ({pendingInvites.length})
          </h4>
          <div className="space-y-4">
            {pendingInvites.map((invite) => (
              <div
                key={invite.inviteId}
                className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 flex items-center gap-3"
              >
                <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-body text-gray-600 dark:text-gray-400 flex-1 truncate">
                  {invite.email}
                </span>
                <span
                  className={`text-meta px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${
                    ROLE_COLORS[(invite.entityRole ?? (invite.functionalRoles?.[0] ?? 'member')) as FunctionalRole | EntityRole]
                  }`}
                >
                  {invite.functionalRoles?.length
                    ? invite.functionalRoles[0].replace(/_/g, ' ')
                    : (invite.entityRole ?? invite.role ?? 'member')}
                </span>
                <button
                  onClick={() => resendMutation.mutate(invite.inviteId)}
                  disabled={resendMutation.isPending}
                  className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                  title="Resend invite"
                >
                  <Send className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setConfirmRevoke(invite)}
                  disabled={revokeMutation.isPending}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                  title="Revoke invite"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
        {editingUser && <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />}
        {confirmRevoke && (
          <ConfirmModal
            title="Revoke invite?"
            body={`The invite sent to ${confirmRevoke.email} will no longer work. You can always send a new one later.`}
            confirmLabel="Revoke Invite"
            confirmClassName="bg-red-600 hover:bg-red-700 text-white"
            onConfirm={() => revokeMutation.mutate(confirmRevoke.inviteId)}
            onCancel={() => setConfirmRevoke(null)}
            loading={revokeMutation.isPending}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
